<template>
  <div>
    <b-spinner
      v-if="loading"
      variant="primary"
      label="Spinning"
      class="position-absolute"
      style="top:50%; left:48%;"
    />
    <template v-else-if="backup">
      <b-card>
        <div class="d-flex justify-content-between flex-wrap">
          <div class="mb-1">
            <h2 class="mb-1">
              {{ backup.title }}
            </h2>
            <b-badge
              pill
              :variant="statusVariants[backup.status]"
            >
              {{ statusLabels[backup.status] }}
            </b-badge>
          </div>
          <div class="site-backup-actions mb-1">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="button"
              @click.prevent="backToBackups"
            >
              Back
            </b-button>
            <b-button
              v-if="$can('restore', 'site-backups')"
              variant="primary"
              :disabled="!restorableSections.length || hasActiveRestore || isCheckingController"
              @click="openRestoreModal"
            >
              <feather-icon
                icon="RotateCcwIcon"
                class="mr-50"
              />
              Restore
            </b-button>
            <b-button
              variant="outline-primary"
              :disabled="!backupData"
              @click="downloadJson"
            >
              <feather-icon
                icon="DownloadIcon"
                class="mr-50"
              />
              Download JSON
            </b-button>
            <b-button
              v-if="$can('delete', 'site-backups')"
              variant="danger"
              @click="$refs.modalDelete.show()"
            >
              <feather-icon
                icon="TrashIcon"
                class="mr-50"
              />
              Delete
            </b-button>
          </div>
        </div>
        <b-row>
          <b-col
            v-for="detail in details"
            :key="detail.label"
            cols="12"
            md="6"
            lg="3"
            class="mb-1"
          >
            <div class="text-muted">
              {{ detail.label }}
            </div>
            <b-link
              v-if="detail.to"
              :to="detail.to"
            >
              {{ detail.value }}
            </b-link>
            <span v-else>{{ detail.value }}</span>
          </b-col>
        </b-row>
      </b-card>

      <b-card
        v-if="restores.length"
        title="Restores"
      >
        <div
          v-for="restore in restores"
          :key="restore.id"
          class="site-backup-restore"
        >
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div class="mb-50">
              <b-badge
                pill
                :variant="restoreStatusVariants[restore.status]"
              >
                {{ restoreStatusLabels[restore.status] }}
              </b-badge>
              <span class="ml-50">{{ formatDateTime(restore.created_at) }}</span>
              <span class="text-muted ml-50">by {{ restore.created_by ? restore.created_by.name : '-' }}</span>
            </div>
            <div class="mb-50">
              <span class="text-muted">Before restore:</span>
              <template v-for="preRestoreBackup in [restore.local_backup, restore.remote_backup].filter(Boolean)">
                <b-link
                  :key="preRestoreBackup.id"
                  class="ml-50"
                  :to="{ name: 'site-summary-backup-details', params: { id: $route.params.id, backupId: preRestoreBackup.id } }"
                >
                  #{{ preRestoreBackup.id }} ({{ statusLabels[preRestoreBackup.status] }})
                </b-link>
              </template>
            </div>
          </div>
          <div
            v-if="restore.error"
            class="text-danger mb-50"
          >
            {{ restore.error }}
          </div>
          <ul
            v-if="restore.result && restore.result.warnings.length"
            class="text-warning pl-1 mb-50"
          >
            <li
              v-for="warning in restore.result.warnings"
              :key="warning"
            >
              {{ warning }}
            </li>
          </ul>
          <div class="site-backup-restore__sections">
            <div
              v-for="section in restoreSectionResults(restore)"
              :key="section.key"
              class="site-backup-restore__section"
            >
              <span>{{ section.title }}</span>
              <b-badge
                pill
                :variant="restoreStatusVariants[section.status]"
                class="site-backup-restore__section-status"
              >
                {{ restoreStatusLabels[section.status] }}
              </b-badge>
              <div
                v-if="section.error"
                class="site-backup-restore__section-details text-danger"
              >
                {{ section.error }}
              </div>
              <ul
                v-if="section.warnings.length"
                class="site-backup-restore__section-details text-warning pl-1 mb-0"
              >
                <li
                  v-for="warning in section.warnings"
                  :key="warning"
                >
                  {{ warning }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </b-card>

      <b-card
        v-if="backupData"
        no-body
      >
        <b-tabs
          card
          pills
          class="site-backup-tabs"
        >
          <b-tab
            v-for="section in includedSections"
            :key="section.key"
            :title="`${section.title} (${sectionRows[section.key].length})`"
          >
            <vue-good-table
              :columns="section.columns"
              :rows="sectionRows[section.key]"
              :sort-options="{ enabled: false }"
              class="site-backup-table"
              compact-mode
            >
              <div slot="emptystate">
                No records in this backup.
              </div>
              <template
                slot="table-row"
                slot-scope="props"
              >
                <span
                  v-if="props.column.secret && props.row[props.column.field]"
                  class="d-flex align-items-center"
                >
                  <span>{{ isPasswordVisible(`${section.key}:${props.row.originalIndex}`) ? props.row[props.column.field] : '••••••••' }}</span>
                  <feather-icon
                    :icon="isPasswordVisible(`${section.key}:${props.row.originalIndex}`) ? 'EyeOffIcon' : 'EyeIcon'"
                    class="cursor-pointer ml-1"
                    @click="togglePasswordVisibility(`${section.key}:${props.row.originalIndex}`)"
                  />
                </span>
                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </b-tab>
          <b-tab
            title="JSON"
            lazy
          >
            <div class="site-backup-json">
              <prism
                language="json"
                :code="prettyJson"
              />
            </div>
          </b-tab>
        </b-tabs>
      </b-card>
      <b-card v-else>
        Backup data is not available: the backup is {{ statusLabels[backup.status].toLowerCase() }}.
      </b-card>
    </template>

    <b-modal
      ref="modalRestoreFinished"
      title="Restore finished"
      centered
      ok-title="Back to Site"
      cancel-title="Stay"
      cancel-variant="outline-secondary"
      @ok="backToSite"
    >
      <p
        v-if="finishedRestore"
        class="mb-0"
      >
        The restore finished with the status
        <b-badge
          pill
          :variant="restoreStatusVariants[finishedRestore.status]"
        >
          {{ restoreStatusLabels[finishedRestore.status] }}
        </b-badge>.
        The result of every section is in the Restores block on this page.
      </p>
    </b-modal>

    <b-modal
      ref="modalDelete"
      title="Are you sure you want to permanently delete this backup?"
      centered
      ok-title="Delete"
      ok-variant="danger"
      @ok="deleteBackup"
    >
      <div>
        Deleted backups cannot be recovered later.
      </div>
    </b-modal>

    <b-modal
      ref="modalControllerOffline"
      title="The controller is offline"
      ok-title="Ok"
      ok-only
      centered
    >
      <p class="mb-0">
        The backup cannot be restored while the site controller is offline:
        the current settings are backed up from it first, and the backup is then written to it.
        Try again once the controller is back online.
      </p>
    </b-modal>

    <b-modal
      ref="modalRestore"
      title="Restore the backup to the site"
      ok-title="Restore"
      ok-variant="danger"
      centered
      :ok-disabled="!restoreSections.length || isRestoreSubmitting || (isWanConfirmationRequired && !isWanConfirmed)"
      @ok.prevent="submitRestore"
    >
      <b-alert
        v-if="controllerMismatch"
        variant="warning"
        class="p-1"
        show
      >
        {{ controllerMismatch }}
      </b-alert>
      <b-form-checkbox
        v-model="allSectionsSelected"
        class="mb-50"
      >
        All sections
      </b-form-checkbox>
      <b-form-checkbox-group
        v-model="restoreSections"
        :options="restoreOptions"
        class="ml-2 mb-1"
        stacked
      />
      <b-alert
        v-if="isWanSelected"
        variant="warning"
        class="p-1"
        show
      >
        <p class="mb-50">
          {{ wanWarning }}
        </p>
        <ul
          v-if="wanSummary.length"
          class="pl-1 mb-50"
        >
          <li
            v-for="item in wanSummary"
            :key="item.label"
          >
            {{ item.label }}:
            <template v-if="item.secret">
              {{ isPasswordVisible(WAN_SECRET_KEY) ? item.value : '••••••••' }}
              <feather-icon
                :icon="isPasswordVisible(WAN_SECRET_KEY) ? 'EyeOffIcon' : 'EyeIcon'"
                class="cursor-pointer ml-50"
                @click="togglePasswordVisibility(WAN_SECRET_KEY)"
              />
            </template>
            <template v-else>
              {{ item.value }}
            </template>
          </li>
        </ul>
        <b-form-checkbox
          v-if="isWanConfirmationRequired"
          v-model="isWanConfirmed"
        >
          I have checked the WAN settings in this backup
        </b-form-checkbox>
      </b-alert>
      <p class="mb-0">
        The selected sections will be written to the controller and the site switches,
        and entries that are not in the backup will be removed.
        Local and Remote backups of the current state are made first, so you can roll back.
      </p>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import {
  BCard, BRow, BCol, BBadge, BButton, BLink, BModal, BSpinner, BTabs, BTab, BAlert, BFormCheckbox, BFormCheckboxGroup,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import 'prismjs'
import 'prismjs/components/prism-json'
import 'prismjs/themes/prism-tomorrow.css'
import Prism from 'vue-prism-component'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  statusLabels, statusVariants, sourceLabels, formatDateTime, restoreStatusLabels, restoreStatusVariants, ACTIVE_RESTORE_STATUSES,
} from './helpers'

const POLL_INTERVAL = 5000

// Index of the Backups tab on the site page.
const BACKUPS_TAB = 6

const WAN_SECTION = 'wan'

const wanTypeLabels = {
  dhcp: 'DHCP Client',
  static: 'Static',
  pppoe: 'PPPoE',
}

const wanWarnings = {
  static: 'The controller WAN will be set to Static with the settings below. Check that the IP address, subnet mask and gateway are still valid: '
    + 'if they are out of date, the controller loses its connection to NetPanel and can only be fixed on site.',
  pppoe: 'The controller WAN will be set to PPPoE with the credentials below. Check that the username and password are still valid: '
    + 'if they are out of date, the controller loses its connection to NetPanel and can only be fixed on site.',
  dhcp: 'The controller WAN will be set to DHCP Client. If the site\'s internet connection needs Static or PPPoE settings, '
    + 'the controller loses its connection to NetPanel and can only be fixed on site.',
}

const capsmanLabels = {
  0: 'Both',
  1: 'New',
  2: 'Old',
}

const dash = value => (value === null || value === undefined || value === '' ? '-' : value)
const yesNo = value => (value ? 'Yes' : 'No')

const column = (field, label, formatFn = dash) => ({
  field,
  label,
  formatFn,
  sortable: false,
})

const sections = [
  {
    key: 'wifi',
    title: 'WiFi',
    columns: [
      column('name', 'Name'),
      column('ssid', 'SSID'),
      column('lan_name', 'LAN'),
      column('encryption', 'Encryption'),
      column('authentication_type', 'Authentication'),
      { ...column('passphrase', 'Password'), secret: true },
      column('hide_ssid', 'SSID Hidden', yesNo),
      column('disabled', 'Disabled', yesNo),
      column('capsman', 'CAPsMAN', value => capsmanLabels[value] || '-'),
      column('channel', 'Channel'),
    ],
    toRow: row => ({
      ...row,
      encryption: row.security ? row.security.encryption : null,
      authentication_type: row.security ? row.security.authentication_type : null,
      passphrase: row.security ? row.security.passphrase : null,
    }),
  },
  {
    key: 'lan',
    title: 'LAN',
    columns: [
      column('name', 'Name'),
      column('vlan_id', 'Vlan ID'),
      column('interface_ip', 'Interface Address'),
      column('subnet_mask', 'Subnet Mask'),
      column('tagged_ports', 'Tagged Ports'),
      column('untagged_ports', 'Untagged Ports'),
      column('comment', 'Comment'),
      column('is_active', 'Enabled', yesNo),
      column('is_default', 'Default', yesNo),
    ],
    toRow: (row, controllerId) => {
      // Ports of the site's switches are kept in the database too, so they are marked with the hardware ID.
      const ports = type => (row.ports || [])
        .filter(port => port.type === type)
        .map(port => (port.hardware_id === controllerId ? port.interface : `${port.interface} (#${port.hardware_id})`))
        .join(', ')

      return {
        ...row,
        tagged_ports: ports('tagged'),
        untagged_ports: ports('untagged'),
      }
    },
  },
  {
    key: WAN_SECTION,
    title: 'WAN',
    columns: [
      column('type', 'Type', value => wanTypeLabels[value] || dash(value)),
      column('interface_ip', 'Interface Address'),
      column('subnet_mask', 'Subnet Mask'),
      column('gateway_ip', 'Gateway Address'),
      column('network_ip', 'Network Address'),
      column('port', 'Port'),
      column('username', 'Username'),
      { ...column('password', 'Password'), secret: true },
      column('is_exported', 'Applied', yesNo),
    ],
  },
  {
    key: 'dhcp_servers',
    title: 'DHCP Servers',
    columns: [
      column('name', 'DHCP Server'),
      column('lan_name', 'LAN'),
      column('gateway_ip', 'Gateway IP'),
      column('subnet_mask', 'Subnet Mask'),
      column('dns1_ip', 'DNS Server'),
      column('dns2_ip', 'DNS Server #2'),
      column('ntp1_ip', 'NTP Server'),
      column('ntp2_ip', 'NTP Server #2'),
      column('wifi_controller_ip', 'WiFi Controller'),
      column('ip_pool_start', 'IP Pool Start'),
      column('ip_pool_end', 'IP Pool End'),
      column('lease_time', 'Lease Time'),
      column('domain_name', 'Domain Name'),
      column('is_active', 'Enabled', yesNo),
    ],
  },
  {
    key: 'active_leases',
    title: 'DHCP Lease Reservations',
    onlySource: 'remote',
    columns: [
      column('address', 'Address'),
      column('mac_address', 'MAC Address'),
      column('server', 'Server'),
      column('address_lists', 'Address Lists'),
      column('comment', 'Comment'),
      column('is_enabled', 'Enabled', yesNo),
    ],
    toRow: row => ({
      ...row,
      is_enabled: !row.is_disabled,
    }),
  },
  {
    key: 'firewall_address_lists',
    title: 'Address Lists',
    columns: [
      column('address', 'IP Address'),
      column('list', 'Address List Name'),
      column('comment', 'Comment'),
      column('is_enabled', 'Enabled', yesNo),
    ],
    toRow: row => ({
      ...row,
      is_enabled: !row.is_disabled,
    }),
  },
  {
    key: 'firewall_nat',
    title: 'NAT Rules',
    columns: [
      column('order', 'Order', value => (Number.isInteger(value) ? value + 1 : '-')),
      column('chain', 'Chain'),
      column('action', 'Action'),
      column('protocol_label', 'Protocol'),
      column('src_address', 'Src Address'),
      column('src_port', 'Src Port'),
      column('dst_address', 'Dst Address'),
      column('dst_port', 'Dst Port'),
      column('to_addresses', 'To Address'),
      column('to_ports', 'To Port'),
      column('in_interface', 'In Interface'),
      column('out_interface', 'Out Interface'),
      column('comment', 'Comment'),
      column('is_enabled', 'Enabled', yesNo),
    ],
    toRow: row => ({
      ...row,
      protocol_label: row.protocol ? `${row.protocol_is_negation ? '!' : ''}${row.protocol}` : 'all',
      is_enabled: !row.is_disabled,
    }),
  },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    BButton,
    BLink,
    BModal,
    BSpinner,
    BTabs,
    BTab,
    BAlert,
    BFormCheckbox,
    BFormCheckboxGroup,
    VueGoodTable,
    Prism,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: true,
      backup: null,
      sections,
      WAN_SECRET_KEY: `${WAN_SECTION}:0`,
      statusLabels,
      statusVariants,
      restoreStatusLabels,
      restoreStatusVariants,
      visiblePasswords: [],
      restores: [],
      restoreSections: [],
      isRestoreSubmitting: false,
      isCheckingController: false,
      isWanConfirmed: false,
      finishedRestore: null,
      pollTimer: null,
    }
  },
  computed: {
    backupData() {
      return this.backup ? this.backup.backup_data : null
    },
    controller() {
      return this.backupData ? this.backupData.controller : null
    },
    sectionRows() {
      const controllerId = this.controller ? this.controller.id : null

      return sections.reduce((rows, section) => {
        const sectionData = (this.backupData && this.backupData.sections[section.key]) || []

        return {
          ...rows,
          [section.key]: section.toRow ? sectionData.map(row => section.toRow(row, controllerId)) : sectionData,
        }
      }, {})
    },
    details() {
      const { site, created_by: createdBy } = this.backup

      return [
        {
          label: 'Site',
          value: site ? `${site.name}${site.is_deleted ? ' (deleted)' : ''}` : '-',
          to: site && !site.is_deleted ? { name: 'site-summary', params: { id: site.id }, query: { tab: BACKUPS_TAB } } : null,
        },
        { label: 'Source', value: sourceLabels[this.backup.source] || this.backup.source },
        { label: 'Created By', value: createdBy ? createdBy.name : '-' },
        { label: 'Created', value: formatDateTime(this.backup.created_at) },
        { label: 'Finished', value: formatDateTime(this.backup.finished_at) },
        { label: 'Controller', value: this.controller ? dash(this.controller.name) : '-' },
        { label: 'Controller Serial', value: this.controller ? dash(this.controller.serial_number) : '-' },
        { label: 'Controller Model', value: this.controller ? dash(this.controller.model) : '-' },
        ...(this.excludedSections.length ? [{
          label: 'Not Included',
          value: this.excludedSections
            .map(section => (section.onlySource && section.onlySource !== this.backup.source
              ? `${section.title} (${sourceLabels[section.onlySource]} backups only)`
              : section.title))
            .join(', '),
        }] : []),
      ]
    },
    // A backup holds only the sections its author had access to.
    includedSections() {
      const backupSections = this.backupData ? this.backupData.sections : {}

      return sections.filter(section => Object.prototype.hasOwnProperty.call(backupSections, section.key))
    },
    excludedSections() {
      return this.backupData ? sections.filter(section => !this.includedSections.includes(section)) : []
    },
    restorableSections() {
      return this.backup ? this.backup.restorable_sections || [] : []
    },
    wanRow() {
      return this.sectionRows[WAN_SECTION][0] || null
    },
    // A backup without WAN settings has the section, but nothing to write.
    selectableSections() {
      return this.restorableSections.filter(key => key !== WAN_SECTION || this.wanRow)
    },
    restoreOptions() {
      return this.includedSections.map(section => {
        let text = section.title

        if (!this.restorableSections.includes(section.key)) {
          text = `${section.title} (not available for your role)`
        } else if (!this.selectableSections.includes(section.key)) {
          text = `${section.title} (no settings in this backup)`
        }

        return {
          value: section.key,
          text,
          disabled: !this.selectableSections.includes(section.key),
        }
      })
    },
    isWanSelected() {
      return this.restoreSections.includes(WAN_SECTION)
    },
    isWanConfirmationRequired() {
      return this.isWanSelected && !!this.wanRow && ['static', 'pppoe'].includes(this.wanRow.type)
    },
    wanWarning() {
      return this.wanRow ? wanWarnings[this.wanRow.type] || wanWarnings.dhcp : ''
    },
    wanSummary() {
      if (!this.wanRow) {
        return []
      }

      if (this.wanRow.type === 'static') {
        return [
          { label: 'Interface Address', value: dash(this.wanRow.interface_ip) },
          { label: 'Subnet Mask', value: dash(this.wanRow.subnet_mask) },
          { label: 'Gateway Address', value: dash(this.wanRow.gateway_ip) },
        ]
      }

      if (this.wanRow.type === 'pppoe') {
        return [
          { label: 'Username', value: dash(this.wanRow.username) },
          { label: 'Password', value: dash(this.wanRow.password), secret: true },
        ]
      }

      return []
    },
    prettyJson() {
      return JSON.stringify(this.backupData, null, 2)
    },
    hasActiveRestore() {
      return this.restores.some(restore => ACTIVE_RESTORE_STATUSES.includes(restore.status))
    },
    allSectionsSelected: {
      get() {
        return this.selectableSections.length > 0 && this.restoreSections.length === this.selectableSections.length
      },
      set(value) {
        this.restoreSections = value ? [...this.selectableSections] : []
      },
    },
    controllerMismatch() {
      const current = this.backup ? this.backup.current_controller : null

      if (!this.controller || !current) {
        return current ? null : 'The site has no controller now, the restore will be rejected.'
      }

      if (this.controller.serial_number === current.serial_number) {
        return null
      }

      return `This backup was made on controller ${dash(this.controller.name)} (${dash(this.controller.serial_number)}, ${dash(this.controller.model)}), `
        + `the site now has ${dash(current.name)} (${dash(current.serial_number)}, ${dash(current.model)}). Ports and WiFi settings may not match.`
    },
  },
  watch: {
    // "Before restore" links open another backup in the same component.
    '$route.params.backupId': 'load',
    isWanSelected(value) {
      if (!value) {
        this.isWanConfirmed = false
      }
    },
  },
  created() {
    this.load()
  },
  beforeDestroy() {
    this.isPollingStopped = true
    clearTimeout(this.pollTimer)
  },
  methods: {
    ...mapActions('siteBackups', ['getItem', 'deleteItem', 'restoreItem', 'getRestores']),
    formatDateTime,
    async load() {
      clearTimeout(this.pollTimer)
      this.loading = true
      this.restores = []
      this.visiblePasswords = []

      try {
        this.backup = await this.getItem(this.$route.params.backupId)
        await this.fetchRestores()
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.$router.push({ name: 'error-404' })
        }
      } finally {
        this.loading = false
      }
    },
    async fetchRestores() {
      clearTimeout(this.pollTimer)

      const activeIds = this.restores.filter(restore => ACTIVE_RESTORE_STATUSES.includes(restore.status)).map(restore => restore.id)

      try {
        this.restores = await this.getRestores(this.backup.id)
      } catch (error) {
        console.warn(error)
      }

      const finishedRestore = this.restores.find(restore => activeIds.includes(restore.id) && !ACTIVE_RESTORE_STATUSES.includes(restore.status))

      if (finishedRestore) {
        this.finishedRestore = finishedRestore
        this.$refs.modalRestoreFinished.show()
      }

      // Restores run in a queued job, so the block refreshes itself until none is active.
      if (!this.isPollingStopped && this.hasActiveRestore) {
        this.pollTimer = setTimeout(() => this.fetchRestores(), POLL_INTERVAL)
      }
    },
    restoreSectionResults(restore) {
      const results = (restore.result && restore.result.sections) || {}

      return [
        ...sections.filter(section => restore.sections.includes(section.key)),
        // Not a backup section: the Public IP service is redeployed after NAT and address list restores.
        ...(results.public_ip ? [{ key: 'public_ip', title: 'Public IP service' }] : []),
      ]
        .map(section => ({
          key: section.key,
          title: section.title,
          status: results[section.key] ? results[section.key].status : restore.status,
          error: results[section.key] ? results[section.key].error : null,
          warnings: results[section.key] ? results[section.key].warnings : [],
        }))
    },
    async openRestoreModal() {
      this.isCheckingController = true

      // The page may have been open for a while, so the controller status is read again.
      try {
        this.backup = await this.getItem(this.backup.id)
      } catch (error) {
        console.warn(error)
      } finally {
        this.isCheckingController = false
      }

      const controller = this.backup.current_controller

      if (controller && !controller.is_online) {
        this.$refs.modalControllerOffline.show()

        return
      }

      // WAN can cut the controller off, so it is never selected until the user picks it.
      this.restoreSections = this.selectableSections.filter(key => key !== WAN_SECTION)
      this.isWanConfirmed = false
      this.$refs.modalRestore.show()
    },
    async submitRestore() {
      if (this.isRestoreSubmitting || !this.restoreSections.length) {
        return
      }

      this.isRestoreSubmitting = true

      try {
        await this.restoreItem({ id: this.backup.id, sections: this.restoreSections })
        this.$refs.modalRestore.hide()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Restore scheduled',
            text: 'The site is locked until the restore finishes.',
            icon: 'RotateCcwIcon',
            variant: 'success',
          },
        })
        await this.fetchRestores()
      } catch (error) {
        console.warn(error)
      } finally {
        this.isRestoreSubmitting = false
      }
    },
    isPasswordVisible(index) {
      return this.visiblePasswords.includes(index)
    },
    togglePasswordVisibility(index) {
      this.visiblePasswords = this.isPasswordVisible(index)
        ? this.visiblePasswords.filter(item => item !== index)
        : [...this.visiblePasswords, index]
    },
    downloadJson() {
      const blob = new Blob([JSON.stringify(this.backupData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = `site-${this.backupData.site.id}-backup-${this.backup.id}.json`
      link.click()
      URL.revokeObjectURL(url)
    },
    backToBackups() {
      this.$router.push({ name: 'site-summary', params: { id: this.$route.params.id }, query: { tab: BACKUPS_TAB } })
    },
    backToSite() {
      this.$router.push({ name: 'site-summary', params: { id: this.$route.params.id } })
    },
    async deleteBackup() {
      try {
        await this.deleteItem(this.backup.id)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Backup deleted',
            icon: 'TrashIcon',
            variant: 'success',
          },
        })
        this.backToBackups()
      } catch (error) {
        console.warn(error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.site-backup-actions {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.site-backup-tabs::v-deep .nav {
  gap: 8px;
}

.site-backup-json {
  max-height: 600px;
  overflow: auto;
}

.site-backup-restore {
  padding: 1rem 0;

  & + & {
    border-top: 1px solid #ebe9f1;
  }

  // One grid for all sections, so every status badge lines up after the longest section title.
  &__sections {
    display: grid;
    grid-template-columns: max-content 1fr;
    align-items: center;
    gap: 0.5rem 1.5rem;
  }

  &__section {
    display: contents;
  }

  &__section-status {
    justify-self: start;
  }

  &__section-details {
    grid-column: 1 / -1;
  }
}

// List pages leak a global middle alignment for vue-good-table, so without this the look depends on navigation.
.site-backup-table::v-deep .vgt-table {
  th,
  td {
    vertical-align: middle;
  }
}
</style>
